import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select } from 'components/common';
import { createAdminUser, getAdminUserById, updateAdminUser } from 'apis/adminUsers';
import { getAdminRoles } from 'apis/adminRoles';
import { useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import '../../../styles/admin-shared.scss';

const UsersForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        fullName: '',
        roleId: 2,
        isActive: true,
    });

    const { data: rolesData = { items: [] } } = useAdminListQuery('roles-options', { pageSize: 100 }, getAdminRoles);
    const roles = rolesData.items || [];

    const { data: detail, error: detailError } = useAdminDetailQuery('users', id, getAdminUserById);
    const createMutation = useAdminCreateMutation('users', createAdminUser);
    const updateMutation = useAdminUpdateMutation('users', updateAdminUser);

    useEffect(() => {
        if (detail) {
            const user = detail.data || detail;
            setFormData({
                username: user.Username || user.username || '',
                email: user.Email || user.email || '',
                password: '', // do not display hashed password
                fullName: user.FullName || user.fullName || '',
                roleId: user.RoleId || user.roleId || user.Roles?.Id || user.roles?.id || 2,
                isActive: (user.IsActive !== undefined ? user.IsActive : user.isActive) !== false,
            });
        }
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Lỗi khi tải thông tin người dùng');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                roleId: Number(formData.roleId),
            };
            if (id && !payload.password) {
                delete payload.password; // optional password update when editing
            }

            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);

            if (res?.success) {
                toast.success(id ? 'Cập nhật người dùng thành công' : 'Tạo người dùng thành công');
                navigate('/admin/users');
            } else {
                toast.error(res?.message || 'Lưu thất bại');
            }
        } catch (err) {
            toast.error(err?.message || 'Có lỗi xảy ra');
        }
    };

    const roleOptions = roles.length > 0
        ? roles.map(r => {
            const rId = r.Id ?? r.id;
            const rName = r.Name || r.name || '';
            const rDesc = r.Description || r.description || '';
            return { value: rId, label: `${rName.toUpperCase()} - ${rDesc}` };
        })
        : [
            { value: 1, label: 'ADMIN - Quản trị hệ thống' },
            { value: 2, label: 'EDITOR - Biên tập nội dung' },
            { value: 3, label: 'VIEWER - Chỉ xem' },
            { value: 4, label: 'HR - Quản lý tuyển dụng' },
        ];

    return (
        <div className="admin-page">
            <PageHeader
                title={id ? 'Chỉnh sửa Người dùng' : 'Thêm Người dùng mới'}
                subtitle="Quản lý tài khoản và phân quyền truy cập hệ thống"
            />
            <form className="admin-form" onSubmit={handleSubmit}>
                <div className="form-grid">
                    <Input
                        label="Tên đăng nhập (Username) *"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        disabled={!!id}
                        required
                    />
                    <Input
                        label="Email *"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />
                    <Input
                        label={id ? "Mật khẩu mới (Bỏ trống nếu không đổi)" : "Mật khẩu *"}
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required={!id}
                    />
                    <Input
                        label="Họ và tên"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                    />
                    <Select
                        label="Vai trò (Role) *"
                        name="roleId"
                        value={formData.roleId}
                        onChange={handleChange}
                        options={roleOptions}
                        required
                    />
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: '24px' }}>
                        <Checkbox
                            label="Kích hoạt tài khoản"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <div className="form-actions" style={{ display: 'flex', gap: '16px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', alignItems: 'center' }}>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/users')}>
                        Hủy
                    </Button>
                    <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                        {id ? 'Cập nhật' : 'Tạo mới'}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default UsersForm;
